"use client";

import { useRef, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { ActionIcon, Portal, Box as MantineBox } from "@mantine/core";
import AutocompleteSearch, { type SearchBoxRef } from "@/components/AutocompleteSearch";
import CrossIcon from "@/icons/Cross";
import SearchIcon from "@/icons/Search";
import styles from "./styles.module.scss";

type MobileNavbarSearchProps = {
  isOpen: boolean;
  onToggle: () => void;
};

const MobileNavbarSearch = ({ isOpen, onToggle }: MobileNavbarSearchProps) => {
  const searchRef = useRef<SearchBoxRef>(null);
  const pathname = usePathname();
  // /appraise has its own image upload + crop flow feeding the appraisal
  // form — hide the navbar's own image search there too, same reason it's
  // already hidden on /search (a nearly-identical paste/crop UI that instead
  // navigates to /search is easy to trigger by mistake).
  const isAppraisePage = pathname?.startsWith("/appraise");
  const isSearchPage = pathname?.startsWith("/search") || isAppraisePage;
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  return (
    <>
      <ActionIcon
        className={styles.searchToggle}
        onClick={onToggle}
        aria-label={isOpen ? "Close search" : "Open search"}
        aria-expanded={isOpen}
        aria-controls="mobile-navbar-search"
        variant="transparent"
        size={44}
      >
        {isOpen ? (
          <CrossIcon width={20} height={20} />
        ) : (
          <SearchIcon color="currentColor" width={20} height={20} />
        )}
      </ActionIcon>

      {isVisible && (
        <Portal>
          <MantineBox
            id="mobile-navbar-search"
            className={styles.mobileSearchContainer}
            data-hide-imagesearch={isSearchPage || undefined}
            data-mobile-search=""
            data-closing={isClosing || undefined}
          >
            <AutocompleteSearch
              ref={searchRef}
              useTypewriter={false}
              navigateOnDropdownSelect={true}
              navigateOnSelect={false}
              disableImageSearch={isAppraisePage}
            />
          </MantineBox>
        </Portal>
      )}
    </>
  );
};

export default MobileNavbarSearch;
